import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Upload, Trash2, FileText } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import DocumentLightbox from './DocumentLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const isImage = (filename = '') => /\.(jpe?g|png)$/i.test(filename);

/**
 * OfferteDocumenten
 * Dialog om documenten (foto's, legplan, datasheets: jpg/png/pdf) te beheren bij een offerte.
 * Props:
 * - open / onOpenChange
 * - offerte: het offerte object (id, offerte_number)
 * - entityType: 'offerte' (reiniging) of 'installation_offerte' (installatie)
 * - onChanged: callback na upload/verwijderen (bv. om badge count te verversen)
 */
const OfferteDocumenten = ({ open, onOpenChange, offerte, entityType, onChanged }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxDoc, setLightboxDoc] = useState(null);
  const fileInputRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    if (!offerte?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/documents?entity_type=${entityType}&entity_id=${offerte.id}`);
      setDocuments(res.data || []);
    } catch (error) {
      toast.error('Kon documenten niet laden');
    } finally {
      setLoading(false);
    }
  }, [offerte?.id, entityType]);

  useEffect(() => {
    if (open) fetchDocuments();
  }, [open, fetchDocuments]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const invalid = files.filter(f => !/\.(jpe?g|png|pdf)$/i.test(f.name));
    if (invalid.length > 0) {
      toast.error(`Alleen JPG, PNG en PDF toegestaan (${invalid.map(f => f.name).join(', ')})`);
      return;
    }

    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('entity_type', entityType);
        formData.append('entity_id', offerte.id);
        formData.append('document_type', 'offerte_bijlage');
        formData.append('file', file);
        await api.post('/documents', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploaded++;
      } catch (error) {
        toast.error(`Upload mislukt: ${file.name}`);
      }
    }
    if (uploaded > 0) {
      toast.success(`${uploaded} document(en) geüpload`);
      fetchDocuments();
      onChanged?.();
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (doc) => {
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success('Document verwijderd');
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      onChanged?.();
    } catch (error) {
      toast.error('Kon document niet verwijderen');
    }
  };

  const openDocument = (doc) => {
    const url = doc.r2_public_url || `${BACKEND_URL}/api/documents/${doc.id}/file`;
    setLightboxDoc({ url, filename: doc.filename });
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="offerte-documenten-dialog">
        <DialogHeader>
          <DialogTitle>Documenten — {offerte?.offerte_number}</DialogTitle>
          <DialogDescription>
            Foto's, legplan en datasheets (JPG, PNG, PDF). Deze kun je als bijlage meesturen met de offerte-mail.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            multiple
            onChange={handleUpload}
            className="hidden"
            id={`offerte-doc-upload-${offerte?.id}`}
          />
          <Button
            variant="outline"
            className="w-full border-dashed"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            data-testid="offerte-doc-upload-btn"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {uploading ? 'Uploaden...' : 'Bestanden uploaden (meerdere mogelijk)'}
          </Button>

          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : documents.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nog geen documenten toegevoegd
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-3 max-h-80 overflow-y-auto p-0.5" data-testid="offerte-doc-list">
              {documents.map((doc) => (
                <div key={doc.id} className="relative group" data-testid={`offerte-doc-${doc.id}`}>
                  <button
                    type="button"
                    onClick={() => openDocument(doc)}
                    className="w-full text-left focus:outline-none focus:ring-2 focus:ring-primary rounded-lg"
                    data-testid={`offerte-doc-open-${doc.id}`}
                  >
                    {isImage(doc.filename) && doc.r2_public_url ? (
                      <img
                        src={doc.r2_public_url}
                        alt={doc.filename}
                        className="w-full h-24 object-cover rounded-lg border hover:opacity-90 transition-opacity"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-24 rounded-lg border bg-muted/40 flex flex-col items-center justify-center gap-1 hover:bg-muted transition-colors">
                        <FileText className="w-7 h-7 text-red-500" />
                        <span className="text-[10px] text-muted-foreground uppercase">
                          {(doc.filename || '').split('.').pop()}
                        </span>
                      </div>
                    )}
                    <p className="text-xs truncate mt-1 text-muted-foreground" title={doc.filename}>
                      {doc.filename}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDelete(doc); }}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Verwijderen"
                    data-testid={`offerte-doc-delete-${doc.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
    
    {/* Popup viewer voor afbeelding/PDF */}
    <DocumentLightbox
      open={!!lightboxDoc}
      onOpenChange={(o) => { if (!o) setLightboxDoc(null); }}
      document={lightboxDoc}
    />
    </>
  );
};

export default OfferteDocumenten;
